/**
 * CFleet Dispatcher - ReadOnlyBanner
 *
 * Persistent, app-wide signal that the organization is in read-only state
 * (trial lapsed without an active subscription, or a payment failed past
 * dunning retries). Operational writes (orders/routes/
 * dispatches) are blocked server-side regardless of this banner; it exists
 * so the block isn't a silent surprise. Settings -> Billing is unaffected —
 * this banner never disables anything, it only informs, and the billing
 * flow (add/update a card) is exactly how an org gets out of this state.
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import useIsOrganizationReadOnly from '../hooks/useIsOrganizationReadOnly';

const ReadOnlyBanner: React.FC = () => {
  const isReadOnly = useIsOrganizationReadOnly();

  if (!isReadOnly) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="read-only-banner"
      className="fixed top-0 inset-x-0 z-[9997] flex items-center justify-center gap-2 px-4 py-1.5 bg-rose-50 border-b border-rose-200 text-rose-800 text-xs font-medium"
    >
      <AlertTriangle size={14} />
      <span>
        Your trial has ended. Orders, routes, and dispatches are read-only until you add a
        payment method in Settings → Billing.
      </span>
    </div>
  );
};

export default ReadOnlyBanner;
